import { useEffect, useState } from "react";
import { Link, Route, Routes } from "react-router-dom";
import { confirmAction, notifyError } from "../lib/ui";
import {
  cancelOrder,
  closeShift,
  currentShift,
  findOrders,
  getReceipt,
  issueOrder,
  openShift,
  archiveOrders,
  pendingOrders,
} from "../api/endpoints";
import ProductPhoto from "../components/ProductPhoto";
import Countdown from "../components/Countdown";
import { HandBlot } from "../components/Hand";
import { ReceiptSheet, ReservationSheet } from "../components/Voucher";
import type { Order, Receipt, Shift } from "../types";
import { categoryLabel } from "../types";
import { haptic } from "../lib/tg";
import AddProductPage from "./AddProductPage";

// Главный экран менеджера: управление сменой и навигация.
function ManagerHome() {
  const [shift, setShift] = useState<Shift | null>(null);
  const [busy, setBusy] = useState(false);

  // Загружаем текущую смену при входе на страницу.
  const refresh = () => currentShift().then(setShift);
  useEffect(() => { refresh(); }, []);

  const open = async () => { setBusy(true); try { setShift(await openShift()); } finally { setBusy(false); } };
  const close = async () => { setBusy(true); try { setShift(await closeShift()); } finally { setBusy(false); } };

  return (
    <div className="container">
      <h1>Менеджер</h1>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Блок смены */}
        <div style={{ padding: 16, background: "var(--secondary-bg)", borderRadius: 16 }}>
          {shift && !shift.closed_at ? (
            <>
              <p className="section-label" style={{ margin: "0 0 4px", color: "var(--text)" }}>🟢 Смена открыта</p>
              <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--hint)" }}>
                с {new Date(shift.opened_at).toLocaleTimeString("ru-RU")} · Продаж: {shift.sales_count} · {Math.round(shift.revenue).toLocaleString("ru-RU")} ₽
              </p>
              <button className="btn btn--secondary" onClick={close} disabled={busy}>Закрыть смену</button>
            </>
          ) : (
            <>
              <p className="section-label" style={{ margin: "0 0 12px" }}>Рабочая смена</p>
              <button className="btn" onClick={open} disabled={busy}>Открыть смену</button>
            </>
          )}
        </div>

        {/* Добавить товар */}
        <div style={{ padding: 16, background: "var(--secondary-bg)", borderRadius: 16 }}>
          <p className="section-label" style={{ margin: "0 0 12px" }}>Каталог</p>
          <Link to="/manager/add" className="btn">+ Добавить товар</Link>
        </div>

        {/* Заказы */}
        <div style={{ padding: 16, background: "var(--secondary-bg)", borderRadius: 16 }}>
          <p className="section-label" style={{ margin: "0 0 12px" }}>Заказы клиентов</p>
          <Link to="/manager/orders" className="btn btn--secondary">Открыть заказы</Link>
          <div style={{ height: 8 }} />
          {/* Архив: активный список теряет заказ в момент выдачи, а чек и талон
              нужны и после неё — сверить оплату, поднять историю выдачи. */}
          <Link to="/manager/archive" className="btn btn--secondary">Архив и чеки</Link>
        </div>
      </div>
    </div>
  );
}

// Плашка подтверждения оплаты перед выдачей товара.
function PaymentConfirmSheet({
  order,
  onConfirm,
  onClose,
}: {
  order: Order;
  onConfirm: (method?: "cash" | "transfer") => Promise<void>;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  // Способ оплаты спрашиваем только у неоплаченных онлайн: там деньги берут
  // здесь и сейчас, и до сих пор этот факт нигде не сохранялся.
  const [method, setMethod] = useState<"cash" | "transfer">("cash");
  const needsMethod = !order.paid_at;

  const handle = async () => {
    setBusy(true);
    try { await onConfirm(needsMethod ? method : undefined); }
    finally { setBusy(false); }
  };
  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "flex-end" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: "100%", background: "var(--bg)", borderRadius: "20px 20px 0 0", padding: "24px 16px 40px" }}
      >
        {order.paid_at ? (
          <>
            <p style={{ margin: "0 0 6px", fontWeight: 700, fontSize: 18 }}>Выдать оплаченный товар?</p>
            <p style={{ margin: "0 0 20px", fontSize: 14, color: "#1a7f1a" }}>
              ✅ Оплачено онлайн · {Math.round(order.amount).toLocaleString("ru-RU")} ₽
            </p>
          </>
        ) : (
          <>
            <p style={{ margin: "0 0 6px", fontWeight: 700, fontSize: 18 }}>Клиент оплатил товар?</p>
            <p style={{ margin: "0 0 20px", fontSize: 14, color: "var(--hint)" }}>
              К оплате: {Math.round(order.amount - (order.deposit_status === "paid" ? order.deposit_amount : 0)).toLocaleString("ru-RU")} ₽
              {order.deposit_status === "paid" && ` (залог ${Math.round(order.deposit_amount).toLocaleString("ru-RU")} ₽ уже оплачен, зачтён)`}
            </p>
          </>
        )}
        {needsMethod && (
          <div className="field">
            <label>Чем расплатился</label>
            <div style={{ display: "flex", gap: 8 }}>
              {([["cash", "Наличные"], ["transfer", "Перевод"]] as ["cash" | "transfer", string][]).map(([v, label]) => (
                <button
                  key={v}
                  className={`chip${method === v ? " chip--on" : ""}`}
                  style={{ flex: 1 }}
                  onClick={() => { haptic.select(); setMethod(v); }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        <button className="btn" style={{ marginBottom: 10 }} onClick={handle} disabled={busy}>
          {busy ? "Выдаём…" : "Да — выдать товар"}
        </button>
        <button className="btn btn--secondary" onClick={onClose}>
          Нет — закрыть
        </button>
      </div>
    </div>
  );
}

function OrderCard({ order, onDone }: { order: Order; onDone: (id: number) => void }) {
  const p = order.product;
  const c = order.client;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [talonOpen, setTalonOpen] = useState(false);

  const doIssue = async (method?: "cash" | "transfer") => {
    try {
      await issueOrder(order.id, method);
      onDone(order.id);
    } catch (e) {
      notifyError(e);
    }
  };

  const doCancel = async () => {
    const ok = await confirmAction(`Снять бронь #${order.id}? Товар вернётся в продажу.`);
    if (!ok) return;
    setBusy(true);
    try {
      await cancelOrder(order.id);
      onDone(order.id);
    } catch (e) {
      notifyError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div style={{ background: "var(--secondary-bg)", borderRadius: 14, marginBottom: 10, overflow: "hidden" }}>
        {/* Товар */}
        <div style={{ display: "flex", gap: 12, padding: 12 }}>
          <ProductPhoto productId={p.id} alt={p.title ?? p.sku} size={72} variant="thumb" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: "0 0 2px", fontWeight: 600, fontSize: 14 }}>
              {p.title ?? categoryLabel(p.category)}
            </p>
            <p className="micro" style={{ margin: "0 0 5px", color: "var(--hint)" }}>
              {p.size} · #{p.sku}
            </p>
            <p style={{ margin: 0, fontWeight: 700, color: "var(--accent)" }}>
              {Math.round(p.price).toLocaleString("ru-RU")} ₽
            </p>
          </div>
        </div>

        {/* Профиль клиента */}
        {c && (
          <div style={{ padding: "8px 12px", borderTop: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
              background: "var(--accent)", display: "flex", alignItems: "center",
              justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 15,
            }}>
              {c.first_name?.slice(0, 1).toUpperCase() ?? "?"}
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {c.first_name ?? "Пользователь"}
              </p>
              <p className="micro" style={{ margin: 0, color: "var(--hint)" }}>
                {c.username ? `@${c.username}` : "без username"}
                {c.phone_last4 && ` · •••• ${c.phone_last4}`}
              </p>
            </div>
          </div>
        )}

        {/* Детали заказа и кнопки */}
        <div style={{ padding: "8px 12px 12px", borderTop: "1px solid var(--border)" }}>
          <p className="micro" style={{ margin: "0 0 8px", color: "var(--hint)" }}>
            {order.type === "purchase" ? "Покупка" : "Бронь"} #{order.id}
            {order.deposit_amount > 0 && ` · залог ${Math.round(order.deposit_amount).toLocaleString("ru-RU")} ₽`}
            {order.expires_at && ` · до ${new Date(order.expires_at).toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}`}
          </p>

          {/* Статус оплаты онлайн + чек. */}
          {order.paid_at && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 0 8px" }}>
              <span className="pill" style={{ background: "rgba(34,139,34,0.14)", color: "#1a7f1a" }}>
                💳 Оплачено онлайн
              </span>
              <button
                onClick={() => getReceipt(order.id).then(setReceipt).catch(notifyError)}
                style={{ background: "none", border: "none", color: "var(--accent)", fontSize: 13, cursor: "pointer", padding: 0, textDecoration: "underline" }}
              >
                Чек
              </button>
            </div>
          )}

          {order.type === "reservation" && order.expires_at && (
            <p style={{ margin: "0 0 8px", fontSize: 13 }}>
              <Countdown expiresAt={order.expires_at} />
            </p>
          )}

          {/* Талон брони — тот же документ, что у клиента: сверять по нему. */}
          {order.type === "reservation" && (
            <button
              className="btn btn--secondary"
              style={{ width: "auto", padding: "8px 14px", fontSize: 13, marginBottom: 8 }}
              onClick={() => { haptic.tap(); setTalonOpen(true); }}
            >
              Талон брони
            </button>
          )}

          <button className="btn" style={{ marginBottom: 8 }} onClick={() => setConfirmOpen(true)} disabled={busy}>
            Выдать клиенту
          </button>

          {order.type === "reservation" && (
            <button
              className="btn btn--secondary"
              style={{ borderColor: "#b00020", color: "#b00020" }}
              onClick={doCancel}
              disabled={busy}
            >
              {busy ? "…" : "Снять бронь"}
            </button>
          )}
        </div>
      </div>

      {confirmOpen && (
        <PaymentConfirmSheet
          order={order}
          onConfirm={doIssue}
          onClose={() => setConfirmOpen(false)}
        />
      )}

      {receipt && <ReceiptSheet receipt={receipt} onClose={() => setReceipt(null)} />}
      {talonOpen && <ReservationSheet order={order} onClose={() => setTalonOpen(false)} />}
    </>
  );
}

// Экран поиска и выдачи заказов.
function ManagerOrders() {
  const [q, setQ] = useState("");
  const [all, setAll] = useState<Order[]>([]);
  const [searched, setSearched] = useState<Order[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    pendingOrders()
      .then(setAll)
      .finally(() => setLoading(false));
  }, []);

  const search = async () => {
    if (!q.trim()) { setSearched(null); return; }
    setBusy(true);
    try { setSearched(await findOrders(q.trim())); }
    finally { setBusy(false); }
  };

  const remove = (id: number) => {
    setAll((a) => a.filter((o) => o.id !== id));
    setSearched((s) => s ? s.filter((o) => o.id !== id) : null);
  };

  const displayed = searched ?? all;

  return (
    <div className="container">
      <h1>Заказы</h1>

      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            placeholder="@username или 4 цифры телефона"
            value={q}
            onChange={(e) => { setQ(e.target.value); if (!e.target.value) setSearched(null); }}
            onKeyDown={(e) => e.key === "Enter" && search()}
            style={{
              flex: 1,
              padding: 12,
              borderRadius: 10,
              border: "1px solid var(--border)",
              background: "var(--secondary-bg)",
              color: "var(--text)",
              fontSize: 16,
              outline: "none",
            }}
          />
          <button
            onClick={search}
            disabled={busy}
            style={{
              padding: "0 20px",
              borderRadius: 10,
              border: "none",
              background: "var(--button)",
              color: "var(--button-text)",
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Найти
          </button>
        </div>
      </div>

      {loading && <p style={{ color: "var(--hint)" }}>Загрузка…</p>}
      {!loading && displayed.length === 0 && (
        <div className="empty">
          <HandBlot variant={searched ? 3 : 1} className="empty__blot" />
          <p style={{ color: "var(--hint)", margin: 0 }}>
            {searched ? "Ничего не найдено." : "Активных заказов нет."}
          </p>
        </div>
      )}
      {displayed.map((o) => (
        <OrderCard key={o.id} order={o} onDone={remove} />
      ))}
    </div>
  );
}

// Архив завершённых заказов: история выдач с чеками и талонами.
function ManagerArchive() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [talon, setTalon] = useState<Order | null>(null);

  useEffect(() => {
    archiveOrders()
      .then(setOrders)
      .catch(notifyError)
      .finally(() => setLoading(false));
  }, []);

  const statusLabel: Record<string, string> = {
    issued: "Выдан",
    expired: "Истёк",
    cancelled: "Снят",
    pending: "Активен",
  };

  return (
    <div className="container">
      <h1>Архив</h1>
      <p style={{ color: "var(--hint)", marginTop: 0, fontSize: 14 }}>
        Завершённые заказы: чек об оплате и талон брони остаются здесь.
      </p>

      {loading && <p style={{ color: "var(--hint)" }}>Загрузка…</p>}
      {!loading && orders.length === 0 && (
        <div className="empty">
          <HandBlot variant={2} className="empty__blot" />
          <p style={{ color: "var(--hint)", margin: 0 }}>Завершённых заказов пока нет.</p>
        </div>
      )}

      {orders.map((o) => (
        <div key={o.id} style={{ background: "var(--secondary-bg)", borderRadius: 14, marginBottom: 10, overflow: "hidden" }}>
          <div style={{ display: "flex", gap: 12, padding: 12 }}>
            <ProductPhoto productId={o.product.id} alt={o.product.title ?? o.product.sku} size={64} variant="thumb" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: "0 0 2px", fontWeight: 600, fontSize: 14 }}>
                {o.product.title ?? categoryLabel(o.product.category)}
              </p>
              <p className="micro" style={{ margin: "0 0 5px", color: "var(--hint)" }}>
                {o.product.size} · #{o.product.sku} · {o.type === "purchase" ? "покупка" : "бронь"} #{o.id}
              </p>
              <p className="micro" style={{ margin: 0, color: "var(--hint)" }}>
                {statusLabel[o.status] ?? o.status}
                {o.issued_at && ` · ${new Date(o.issued_at).toLocaleDateString("ru-RU")}`}
                {o.client && ` · ${o.client.username ? "@" + o.client.username : o.client.first_name ?? "клиент"}`}
              </p>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, padding: "0 12px 12px", flexWrap: "wrap" }}>
            {/* Выданный заказ оплачен при получении — чек по нему тоже есть. */}
            {(o.paid_at || o.status === "issued") && (
              <button
                className="btn btn--secondary"
                style={{ width: "auto", padding: "8px 14px", fontSize: 13 }}
                onClick={() => { haptic.tap(); getReceipt(o.id).then(setReceipt).catch(notifyError); }}
              >
                Чек
              </button>
            )}
            {o.type === "reservation" && (
              <button
                className="btn btn--secondary"
                style={{ width: "auto", padding: "8px 14px", fontSize: 13 }}
                onClick={() => { haptic.tap(); setTalon(o); }}
              >
                Талон
              </button>
            )}
          </div>
        </div>
      ))}

      {receipt && <ReceiptSheet receipt={receipt} onClose={() => setReceipt(null)} />}
      {talon && <ReservationSheet order={talon} onClose={() => setTalon(null)} />}
    </div>
  );
}

// Роутинг внутри раздела менеджера.
export default function ManagerPage() {
  return (
    <Routes>
      <Route index element={<ManagerHome />} />
      <Route path="add" element={<AddProductPage />} />
      <Route path="orders" element={<ManagerOrders />} />
      <Route path="archive" element={<ManagerArchive />} />
    </Routes>
  );
}
